import { useTour } from '@tecsinapse-org/portal-menu-kit'
import { useCallback, useEffect, useRef } from 'react'
import type {
  OnboardingByIdQuery,
  OnboardingByIdQueryVariables,
  SetOnboardingIdMutation,
  SetOnboardingIdMutationVariables
} from '@/core/types/graphql'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { request } from '../utils/request'

const QUERY_ONBOARDING_BY_ID = gql`
  query OnboardingByID($id: ID!) {
    currentUserOnboardingIsSeen(id: $id)
  }
`

const MUTATION_SET_ONBOARDING_ID = gql`
  mutation SetOnboardingID($id: ID!) {
    setCurrentUserOnboardingSeen(id: $id)
  }
`

export const useOnboarding = (id: string) => {
  const client = useQueryClient()

  const {
    data,
    isLoading: loading,
    isError
  } = useQuery({
    queryKey: ['onBoardingById', id],
    queryFn: async () =>
      await request<OnboardingByIdQuery, OnboardingByIdQueryVariables>(
        QUERY_ONBOARDING_BY_ID,
        { variables: { id } }
      )
  })

  const setSeen = useMutation({
    mutationKey: ['setOnBoardingId'],
    mutationFn: async ({
      variables
    }: {
      variables: SetOnboardingIdMutationVariables
    }) =>
      await request<SetOnboardingIdMutation, SetOnboardingIdMutationVariables>(
        MUTATION_SET_ONBOARDING_ID,
        { variables }
      ),
    onSuccess: async () => {
      await client.refetchQueries({ queryKey: ['onBoardingById'] })
    }
  })
  const { mutateAsync } = setSeen

  const { setSteps, setIsOpen, setCurrentStep } = useTour()
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setOpen = useCallback(
    (delay = 1000) => {
      if (openTimer.current !== null) {
        clearTimeout(openTimer.current)
      }

      setCurrentStep(0)
      openTimer.current = setTimeout(() => {
        openTimer.current = null
        setIsOpen(true)
      }, delay)
    },
    [setCurrentStep, setIsOpen]
  )

  useEffect(() => {
    return () => {
      if (openTimer.current !== null) {
        clearTimeout(openTimer.current)
        openTimer.current = null
      }
      setIsOpen(false)
    }
  }, [setIsOpen])

  const save = useCallback(async () => {
    await mutateAsync({ variables: { id } })
  }, [mutateAsync, id])

  return {
    setOpen,
    setSeen,
    currentUserOnboardingIsSeen: data?.currentUserOnboardingIsSeen,
    canOpen: !loading && !isError && data?.currentUserOnboardingIsSeen !== true,
    setSteps,
    loading,
    save,
    setCurrentStep
  }
}
